// ============================================
// VOLUMETRIA DO DOADOR VIVO DE FÍGADO — B351 (AI_152)
// ============================================
// O médico digita volumes e pesos; o sistema DERIVA as porcentagens. Mesmo padrão
// do MELD e da abstinência alcoólica (B311): a conta fica em fonte única, testada,
// e os limiares em constantes — para trocar num lugar só se o serviço mudar de
// critério.
//
// ⚠️ Os limiares abaixo são os de uso corrente na literatura de LDLT (consenso
// ILTS-iLDLT 2026, já no corpus RAG). São BALIZAS, não regras: a decisão é do
// cirurgião, caso a caso. Por isso a função apenas CLASSIFICA — nunca bloqueia.

/** GRWR mínimo usualmente aceito (%). Abaixo disso, risco de small-for-size. */
export const GRWR_MINIMO = 0.8;

/** Remanescente mínimo do doador (%). É o limiar que protege QUEM DOA. */
export const REMANESCENTE_MINIMO = 30;

/** Divisão segura: devolve null em vez de Infinity/NaN. */
function razao(numerador?: number | null, denominador?: number | null): number | null {
  const n = Number(numerador);
  const d = Number(denominador);
  if (!Number.isFinite(n) || !Number.isFinite(d)) return null;
  if (n <= 0 || d <= 0) return null;
  return n / d;
}

/** Arredonda para 2 casas sem o erro de ponto flutuante do toFixed encadeado. */
function duasCasas(v: number): number {
  return Math.round(v * 100) / 100;
}

/**
 * GRWR — graft-to-recipient weight ratio, em %.
 * volume do enxerto (cm³) ÷ peso do receptor (kg × 1000).
 *
 * ⚠️ B351-FU (AI_152): a entrada é o VOLUME em cm³, porque é o que a tomografia
 * de volumetria reporta — pedir gramas obrigaria o médico a converter a cada
 * laudo, e é aí que nasce erro de digitação. O GRWR é definido por PESO, e a
 * prática de LDLT usa a equivalência **1 cm³ ≈ 1 g** (densidade do fígado ~1,0).
 * A conversão é assumida aqui, explicitamente, em vez de ficar na cabeça de quem
 * digita.
 */
export function calcularGRWR(
  volumeEnxertoCm3?: number | null,
  pesoReceptorKg?: number | null
): number | null {
  const r = razao(volumeEnxertoCm3, Number(pesoReceptorKg) * 1000);
  return r === null ? null : duasCasas(r * 100);
}

/**
 * Remanescente do DOADOR, em % do fígado total.
 * (fígado total − enxerto) ÷ fígado total. Como é uma RAZÃO entre volumes, o
 * resultado é o mesmo em cm³ ou em g — a unidade se cancela.
 */
export function calcularRemanescente(
  volumeFigadoTotalCm3?: number | null,
  volumeEnxertoCm3?: number | null
): number | null {
  const total = Number(volumeFigadoTotalCm3);
  const enxerto = Number(volumeEnxertoCm3);
  if (!Number.isFinite(total) || !Number.isFinite(enxerto)) return null;
  if (total <= 0 || enxerto <= 0) return null;
  // Enxerto maior que o fígado inteiro é erro de digitação, não um remanescente
  // negativo — devolver null é mais honesto que exibir "-12%".
  if (enxerto >= total) return null;
  return duasCasas(((total - enxerto) / total) * 100);
}

/** IMC do doador (kg/m²). Altura aceita em metros (1,75) ou centímetros (175). */
export function calcularIMC(
  pesoKg?: number | null,
  altura?: number | null
): number | null {
  const p = Number(pesoKg);
  let a = Number(altura);
  if (!Number.isFinite(p) || !Number.isFinite(a) || p <= 0 || a <= 0) return null;
  if (a > 3) a = a / 100; // veio em centímetros
  return duasCasas(p / (a * a));
}

export type Classificacao = "adequado" | "limitrofe" | "insuficiente" | null;

/**
 * Classifica o GRWR contra o limiar. "limítrofe" é a faixa em que centros usam
 * modulação de fluxo — decisão do cirurgião, não do sistema.
 */
export function classificarGRWR(grwr: number | null): Classificacao {
  if (grwr === null) return null;
  if (grwr >= GRWR_MINIMO) return "adequado";
  if (grwr >= 0.6) return "limitrofe";
  return "insuficiente";
}

/** Classifica o remanescente do doador contra o limiar de segurança. */
export function classificarRemanescente(remanescente: number | null): Classificacao {
  if (remanescente === null) return null;
  if (remanescente >= REMANESCENTE_MINIMO) return "adequado";
  if (remanescente >= 25) return "limitrofe";
  return "insuficiente";
}

// ════════════════════════════════════════════════════════════════════════════
// COERÊNCIA ENTRE O ENXERTO DECLARADO E A PROPORÇÃO MEDIDA — B355 (AI_153)
// ════════════════════════════════════════════════════════════════════════════
// Nasceu de um caso real. O laudo da angio-TC de uma doadora trazia:
//     Total: 1.463 cm³ · "Lobo direito: 542 cm³" · Lobo esquerdo com VHM: 673 cm³
// A palavra "direito" na segunda linha era ERRO DE DIGITAÇÃO do laudo — as duas
// medidas eram do lobo ESQUERDO, sem e com a veia hepática média (confirmado por
// Leonardo no vídeo do estudo: a mesma peça, o entalhe medial preenchido). O
// enxerto real (lobo direito COM a VHM) era 1.463 − 542 = 921 cm³.
//
// Uma palavra trocada num laudo contaminou tudo o que o sistema derivou dela:
//     GRWR          0,90% (limítrofe, quase small-for-size)  →  real 1,53%
//     Remanescente  62,95% (folgado)                          →  real 37,0%
// Os dois erros na MESMA direção: enxerto parecendo pequeno demais, remanescente
// parecendo generoso demais. Quem decidisse por esses números hesitaria por um
// GRWR que nunca foi limítrofe e relaxaria sobre um remanescente que nunca foi
// de 63%.
//
// A análise de IA detectou o sintoma ("lobo direito com 37% é implausível") mas
// não podia consertar: recebeu o rótulo errado como entrada. Por isso a trava
// vive AQUI, onde o dado é digitado — rotular o dado na fonte em vez de esperar
// que o modelo raciocine sobre ele depois.
//
// ⚠️ É AVISO, nunca bloqueio: anatomia atípica existe, e quem decide é o
// cirurgião. A trava só diz "confira o laudo", que é exatamente o que teria
// bastado neste caso.

/** Faixa plausível do enxerto, em % do fígado total, por território doado. */
const FAIXA_PLAUSIVEL: Record<string, { min: number; max: number; tipico: string }> = {
  "lobo direito": { min: 50, max: 80, tipico: "~60-65%" },
  "lobo esquerdo": { min: 25, max: 49, tipico: "~30-40%" },
  "segmento lateral esquerdo (ii-iii)": { min: 10, max: 30, tipico: "~20-25%" },
};

export interface AvisoVolumetria {
  /** Percentual do enxerto sobre o total, como digitado. */
  percentual: number;
  mensagem: string;
}

/**
 * Confere se o enxerto digitado é plausível para o território declarado.
 *
 * @returns null quando está coerente, quando falta dado, ou quando o território
 *          não é um dos conhecidos — a trava nunca inventa suspeita.
 */
export function conferirCoerenciaEnxerto(
  loboDoado?: string | null,
  volumeFigadoTotalCm3?: number | null,
  volumeEnxertoCm3?: number | null
): AvisoVolumetria | null {
  const faixa = FAIXA_PLAUSIVEL[String(loboDoado || "").trim().toLowerCase()];
  if (!faixa) return null;

  const total = Number(volumeFigadoTotalCm3);
  const enxerto = Number(volumeEnxertoCm3);
  if (!Number.isFinite(total) || !Number.isFinite(enxerto)) return null;
  if (total <= 0 || enxerto <= 0) return null;

  const percentual = duasCasas((enxerto / total) * 100);
  if (percentual >= faixa.min && percentual <= faixa.max) return null;

  // O complemento é a pista concreta: no caso real, 1.463 − 542 = 921 cm³, que é
  // o enxerto de verdade. Oferecer o número poupa a conta de cabeça.
  const complemento = Math.round(total - enxerto);
  const pctComplemento = duasCasas((complemento / total) * 100);
  const complementoCabe =
    pctComplemento >= faixa.min && pctComplemento <= faixa.max;

  const base =
    `O enxerto digitado dá ${percentual}% do fígado total, e ${loboDoado} costuma ` +
    `ficar em ${faixa.tipico}. Confira o laudo antes de usar o GRWR e o remanescente.`;

  const pista = complementoCabe
    ? ` O que sobra (${complemento} cm³, ${pctComplemento}%) cai na faixa esperada — ` +
      `verifique se o volume digitado é o do enxerto ou o do lado que FICA com quem doa, ` +
      `e se o laudo mede o mesmo território com e sem a veia hepática média.`
    : "";

  return { percentual, mensagem: base + pista };
}
